import { isPortrait } from "@multimediallc/web-utils"
import { isMobileDevice } from "@multimediallc/web-utils/modernizr"
import { EventRouter } from "../../../common/events"
import { isDesktopDmActive, isMobileDmActive } from "../../../common/featureFlagUtil"
import { openMessagingConversation } from "../../../common/mobilelib/userActionEvents"
import { screenOrientationChanged } from "../../../common/mobilelib/windowOrientation"
import { uuidv4 } from "../../../common/uuid"
import { createDmWindowRequest } from "../pm/dmWindowsManager"
import { ReactComponentRegistry } from "../ReactRegistry"
import { messagingProps } from "./messagingUtil"
import type { ReactComponent } from "../ReactRegistry"

export const openMessagingInbox = new EventRouter<undefined>("openMessagingInbox")
export const openMessagingUnified = new EventRouter<string>("openMessagingUnified")
export const messagingInboxClosed = new EventRouter<void>("messagingInboxClosed")

enum View {
    Mobile = "mobile",
    DesktopInbox = "desktop_inbox",
    DesktopConversation = "desktop_conversation",
    DesktopUnified = "desktop_unified",
}

type MountedComponent = {
    component: ReactComponent
    container: HTMLDivElement
}

type MessagingInstance = MountedComponent & {
    hidden: boolean;
    view: View;
    initialEntry?: string;
}

class MessagingManager {
    private instances = new Map<string, MessagingInstance>()
    private landscapeModal?: MountedComponent
    private orientationOpenPending = false

    constructor() {
        // catch all condition to prevent new messaging for now
        if (!isMobileDmActive()) {
            return
        }
        this.bindListeners()
    }

    private bindListeners() {
        openMessagingInbox.listen(() => {
            this.createInboxInstance()
        })

        // mobile only event for now
        openMessagingConversation.listen((username: string) => {
            this.createInstance({
                view: View.Mobile,
                onClose: () => { this.destroyAll() },
                initialEntry: `/messages/${username}`,
                ...messagingProps,
            })
        })

        // opening a conversation from the desktop
        createDmWindowRequest.listen((username: string) => {
            const messagingInstanceId = `messaging-${username}`
            if(!isDesktopDmActive() || this.isInstanceVisible(messagingInstanceId)) {
                return
            }
            this.destroyAllDesktopConversations()
            this.createInstance({
                view: View.DesktopConversation,
                onClose: (messagingInstanceId: string) => { this.destroyInstance(messagingInstanceId) },
                initialEntry: `/messages/${username}`,
                messagingInstanceId,
                ...messagingProps,
            })
        })

        // desktop unified popout
        openMessagingUnified.listen((username: string) => {
            if(!isDesktopDmActive()) {
                return
            }
            this.destroyAll()
            this.createInstance({
                view: View.DesktopUnified,
                initialEntry: `/messages/${username ?? ""}`,
                onClose: () => {},
                ...messagingProps,
            })
        })

        screenOrientationChanged.listen(() => {
            if(this.landscapeModal && isPortrait()) {
                this.hideLandscapeModal()
            }

            if (this.instances.size === 0) {
                return
            }
            const instanceId = this.instances.keys().next().value
            const instance = this.instances.get(instanceId)
            if (!instance) {return}

            if (!instance.hidden && !isPortrait()) {
                this.showLandscapeModal()
                this.hideInstance(instanceId)
                this.orientationOpenPending = true
            }

            if (this.orientationOpenPending && isPortrait()) {
                this.orientationOpenPending = false
                this.showInstance(instanceId)
            }
        })
    }

    /**
     * Creates and mounts a Messaging component instance to the document body.
     *
     * @param {object} props - Configuration properties for the messaging instance.
     * @returns {{ messagingInstanceId: string }} An object containing the ID of the created messaging instance.
     */
    createInstance(props: Record<string, unknown> & { messagingInstanceId?: string, view: View }): { messagingInstanceId: string } {
        const messagingInstanceId = props.messagingInstanceId ?? `messaging-${uuidv4()}`

        let container = document.getElementById("content") as HTMLDivElement | null

        if (!container) {
            container = document.createElement("div")
            container.dataset.messagingId = messagingInstanceId
            document.body.appendChild(container)
        }

        const MessagingClass = ReactComponentRegistry.get("Messaging")
        const messagingComponent = new MessagingClass({ ...messagingProps, ...props, messagingInstanceId }, container)
        this.instances.set(messagingInstanceId, { component: messagingComponent, container, hidden: false, view: props.view })

        return { messagingInstanceId }
    }

    destroyInstance(id: string): void {
        const instance = this.instances.get(id)
        if (!instance) {return}

        if (typeof instance.component.dispose === "function") {
            instance.component.dispose()
        }
        document.body.removeChild(instance.container)
        this.instances.delete(id)
    }

    isInstanceVisible(id: string): boolean {
        const instance = this.instances.get(id)
        return instance !== undefined && !instance.hidden
    }

    hideInstance(id: string): void {
        const instance = this.instances.get(id)
        if (!instance) {return}

        instance.container.style.display = "none"
        instance.hidden = true
    }

    showInstance(id: string): void {
        const instance = this.instances.get(id)
        if (!instance) {return}

        instance.container.style.display = "block"
        instance.hidden = false
    }

    destroyAll(): void {
        for (const [id] of this.instances) {
            this.destroyInstance(id)
        }
    }

    showLandscapeModal(): void {
        if (this.landscapeModal) {
            return
        }

        const container = document.createElement("div")
        document.body.appendChild(container)

        const onClose = () => {
            this.hideLandscapeModal()
            this.orientationOpenPending = false
        }

        const LandscapeModalClass = ReactComponentRegistry.get("MessagingLandscapeModal")
        const landscapeModalComponent = new LandscapeModalClass({ onClose }, container)
        this.landscapeModal = { component: landscapeModalComponent, container }
    }

    hideLandscapeModal(): void {
        if (!this.landscapeModal) {
            return
        }

        const { component, container } = this.landscapeModal
        if (typeof component.dispose === "function") {
            component.dispose()
        }
        document.body.removeChild(container)
        this.landscapeModal = undefined
    }

    private createInboxInstance(): void {
        if(!isPortrait()) {
            this.showLandscapeModal()
            return
        }

        // if instance already exists, show it
        const hasExistingInstance = this.instances.size > 0
        if (hasExistingInstance) {
            const existingInstanceId = this.instances.keys().next().value
            this.showInstance(existingInstanceId)
            return
        }

        const messagingInstanceId = `messaging-${uuidv4()}`
        const viewType = isMobileDevice() ? View.Mobile : View.DesktopInbox

        this.createInstance({
            view: viewType,
            messagingInstanceId,
            onClose: () => {
                this.hideInstance(messagingInstanceId)
                messagingInboxClosed.fire()
            },
            ...messagingProps,
        })
    }

    destroyAllDesktopConversations(): void {
        const idsToDestroy: string[] = []
        for (const [id, instanceData] of this.instances) {
            if (instanceData.view === View.DesktopConversation) {
                idsToDestroy.push(id)
            }
        }
        idsToDestroy.forEach(id => this.destroyInstance(id))
    }
}

export const messagingManager = new MessagingManager()
